import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@front/shared/util-environment';
import {
  ChangePasswordRequest,
  EditUserRequest,
  Generate2faResponse,
  LoginRequest,
  LoginResponse,
  RegistrationRequest,
  ResetPasswordRequest,
  User,
  UserPublic,
  Verified2faResponse,
  Verify2faRequest,
} from '@front/shared/data-access-cms';
import { Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #http = inject(HttpClient);
  #env = inject(ENVIRONMENT);

  // URLs
  readonly #USERS_URL = `${this.#env.cmsApiUrl}/users`;
  readonly #AUTH_URL = `${this.#env.cmsApiUrl}/auth`;

  // Errors
  readonly MISSING_JWT_ERROR = 'JWT token is missing';

  getUserInfo(): Observable<UserPublic | null> {
    const url = `${this.#USERS_URL}/me`;
    return this.#http.get<UserPublic>(url);
  }

  login(loginRequest: LoginRequest) {
    const url = `${this.#AUTH_URL}/local`;
    return this.#http.post<LoginResponse>(url, loginRequest);
  }

  performRefresh(
    refreshToken: string | null,
  ): Observable<Verified2faResponse | null> {
    if (!refreshToken) {
      return throwError(() => new Error(this.MISSING_JWT_ERROR));
    }
    const url = `${this.#AUTH_URL}/refresh-token`;
    return this.#http.post<Verified2faResponse>(url, {
      refreshToken,
    });
  }

  register(registrationRequest: RegistrationRequest) {
    const url = `${this.#AUTH_URL}/local/register`;
    return this.#http.post<User>(url, registrationRequest);
  }

  forgotPassword(email: string) {
    const url = `${this.#AUTH_URL}/forgot-password`;
    return this.#http.post(url, { email });
  }

  changePassword(changePasswordRequest: ChangePasswordRequest) {
    const url = `${this.#AUTH_URL}/change-password`;
    return this.#http.post(url, changePasswordRequest);
  }

  resetPassword(resetPasswordRequest: ResetPasswordRequest) {
    const url = `${this.#AUTH_URL}/reset-password`;
    return this.#http.post(url, resetPasswordRequest);
  }

  generateSecret2fa(secretData: { code: string; OTPName: string }) {
    const url = `${this.#AUTH_URL}/generate-2fa`;
    return this.#http.post<Generate2faResponse>(url, {
      ...secretData,
    });
  }

  verify2fa(verify2faRequest: Verify2faRequest) {
    const url = `${this.#AUTH_URL}/verify-2fa`;
    return this.#http.post<Verified2faResponse>(url, verify2faRequest);
  }

  // use id, documentID doesn't work, although Strapi docs say so
  editUser(
    userId: string | number,
    editUserRequest: EditUserRequest,
  ): Observable<UserPublic | null> {
    const url = `${this.#USERS_URL}/${userId}`;
    return this.#http.put<UserPublic>(url, editUserRequest);
  }

  validateToken(
    accessToken: string,
    provider = 'google',
  ): Observable<Verified2faResponse | null> {
    const url = `${this.#AUTH_URL}/${provider}/callback`;
    const params = new HttpParams().append('access_token', accessToken);
    return this.#http.get<Verified2faResponse>(url, {
      params,
    });
  }

  acceptTerms(): Observable<{ termsAccepted: boolean }> {
    const url = `${this.#USERS_URL}/accept-terms`;
    return this.#http.post<{ termsAccepted: boolean }>(url, {});
  }
}
